import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StaffList } from "../StaffList/StaffList";
import { AddingEmployee } from "../AddingEmployee/AddingEmployee";
import { ReactComponent as TickIcon } from "../../images/TickIcon.svg";

import "./StaffTable.styles.sass";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import NewCheckBox from "components-new/NewCheckBox";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { changeActiveFilter } from "toolkit/employees/employees.thunks";
import { usePagination } from "hooks/usePagination";
import { setEmployees } from "toolkit/employees/employees.slice";
import { useLocation } from "react-router-dom";
import {userAPI} from "../../libs/apiClient/User";

const FilterColumn: React.FC<any> = ({ title, radios = [], archive }) => {
	const ref = useRef<HTMLTableCellElement | null>(null);

	const dispatch = useAppDispatch();

	const [open, setOpen] = useState<boolean>(false);
	useOutsideAlerter(ref.current, () => setOpen(false));

	return (
		<th
			className={`staff-table-head-row-item menus${open ? " staff-table-head-row-item-active" : ""}`}
			ref={ref}
			onClick={(e) => {
				e.stopPropagation();
				setOpen(true);
			}}>
			<div className="staff-table-head-row-item-wrp">
				<span className="staff-table-head-row-item-name">{title}</span>
				<TickIcon className={"staff-table-head-row-item-icon"} />
			</div>

			{open && (
				<div className="staff-table-head-row-dropDown">
					{radios.map((_: any, index: number) => {
						return (
							<label key={index} className={"staff-table-head-row-dropDown-item"}>
								<NewCheckBox
									name={_.name}
									checked={_.active}
									onChange={() => {
										dispatch(changeActiveFilter({ archive, item: _ }));
									}}
									type={"radio"}
								/>
								{_.name}
							</label>
						);
					})}
				</div>
			)}
		</th>
	);
};

interface StaffTableProps {
	archive?: boolean;
	select: boolean;
	addingEmployee?: any;
	addingOrgs?: any[];
	setAddingEmployee?: any;
	query?: string;
	orgs?: any[];
	outline: (id: number) => string;
}

export const StaffTable: FC<StaffTableProps> = ({
	archive = false,
	select,
	addingOrgs,
	addingEmployee,
	setAddingEmployee,
	query,
	outline,
}) => {
	const dispatch = useAppDispatch();

	/**
	 * здесь изначально устанавливается первая страница и количество выводимых элементов
	 */
	const [params, setParams] = useState<{
		page: number;
		num: number;
		filter_column?: string | null;
		filter_value?: string | null;
		query?: "";
	}>({
		page: 1,
		num: 30,
		filter_column: null,
		filter_value: null,
		query: "",
	});

	const location = useLocation();

	const org = useMemo(() => {
		return location.search.replace(/(\D+)/g, "");
	}, [location.search]);
	const { status, department, position, relationship } = useAppSelector((state) => state.employess.employeesFilters);

	/**
	 * если активная, список сотрудников переписывается, а если не активная, дописывает значения в массив пользователей
	 */
	const [changingData, setChangingData] = useState(true);

	/**
	 * нужен для первой загрузки для фильтров, работает вместе с changingData, если они оба активные то фильтры создаются
	 */
	const [tempFilter, setTempFilter] = useState(true);

	/**
	 * хранит прокручиваемый контейнер, для подгрузки данных
	 */
	const scrollData = useRef<HTMLTableSectionElement | null>(null);

	/**
	 * функция обработки ответа от сервера
	 */
	const then = useCallback(
		(data: any) => {
			if (data.list.length < params.num || !data.list.length) {
				/* если ответа нет или он не полный то устанавливается флаг прекращения запросов */
				setIsFinally(true);
			} else {
				/* иначе устанавливается следующая страница для запроса */
				setParams((prevState: any) => ({
					...prevState,
					page: prevState.page + 1,
				}));
			}

			dispatch(setEmployees({ data, change: params.page === 1, filterData: tempFilter }));

			setTempFilter(false);

			/* если запрос прошел с полным ответом, переключилась страница, то выключается флаг для перезаписи пользователей */
			if (changingData || params.num !== 1) {
				setChangingData(false);
			}

			//eslint-disable-next-line
		},
		//eslint-disable-next-line
		[changingData, tempFilter, params],
	);

	const closeAddingEmployee = () => {
		setAddingEmployee(false);
	};

	const linkEmployee = useMemo(() => {
		let link = `/employees/long?org=${org}`;
		if (archive) {
			return `/employees/long/archived?org=${org}`;
		}
		if (select) {
			link += "/unbound";
		}
		return link;
	}, [select, archive, org]);

	useEffect(() => {
		if (!fetch) {
			const arr = [department, relationship, position, status].flat().filter((_) => _.active);
			const active = arr.find((_) => _.id > 0);
			let dopFilter: { filter_column: string | null; filter_value: string | null } = {
				filter_column: null,
				filter_value: null,
			};
			if (active && active.id > 0) {
				dopFilter.filter_column = active.column;
				dopFilter.filter_value = active.name;
			}
			setParams((prevState: any) => ({ ...prevState, page: 1, query, ...dopFilter }));
			setChangingData(true);
			setFetch(true);
			setIsFinally(false);
		}
		//eslint-disable-next-line
	}, [archive, select, query, org, department, relationship, position, status]);

	const reload = () => {
		setParams((prevState) => ({ ...prevState, page: 1 }));
		setIsFinally(false);
		setFetch(true);
	};
	const { setIsFinally, setFetch, fetch } = usePagination<any>(
		linkEmployee,
		{
			...params,
			filter_active: !archive,
		},
		then,
		scrollData,
		[params, linkEmployee],
	);
	const [readCard, setReadCard] = useState(false);
	useEffect(() => {
		userAPI.permissionsCheckCreate(0, ["system.employee.read"]).then(({ data }) => {
			setReadCard(!!data.length);
		});
	}, []);
	return (
		<main className="staff-content">
			{addingEmployee && (
				<AddingEmployee archive={archive} params={reload} orgs={addingOrgs || []} close={closeAddingEmployee} />
			)}
			<section className="staff">
				<table className="staff-table">
					<thead className="staff-table-head">
						<tr className="staff-table-head-row">
							<th className="staff-table-head-row-item">фио сотрудника</th>
							<th className="staff-table-head-row-item">табельный номер</th>
							<FilterColumn
								archive={archive}
								params={params}
								radios={relationship}
								title={"вид отношений с организацией"}
							/>
							<FilterColumn archive={archive} params={params} radios={position} title={"доложность"} />
							<FilterColumn archive={archive} params={params} radios={department} title={"подразделение"} />
							<th className="staff-table-head-row-item">дата поступления</th>
							<th className="staff-table-head-row-item">дата увольнения</th>
							{!archive && <FilterColumn archive={archive} params={params} radios={status} title={"статус"} />}
							<th className="staff-table-head-row-item">организация-работодатель</th>
							<th className="staff-table-head-row-item"></th>
						</tr>
					</thead>
					<StaffList read={readCard} outline={outline} ref={scrollData} archive={archive} select={select} />
				</table>
			</section>
		</main>
	);
};
